import { memo, useCallback } from 'react'
import { Box, Divider, Skeleton } from '@mui/material'
import type { PropertyColumn } from '@/objects/collection-view/grid/PropertyColumn.tsx'
import type { ObjectModel } from '@/core/gql/gql-models-override.ts'
import type { PropertyValue } from '@/objects/gql/PropertyValue.ts'
import { GridCell } from '@/objects/collection-view/grid/cells/GridCell.tsx'
import { useDiRef } from '@/common/react/di-hooks.ts'
import type { ObjectsService } from '@/objects/services/ObjectsService.ts'
import { collectionPageConfig as config } from '@/objects/collection-view/collection-page-config.ts'
import { globalCssVars } from '@/core/theme/GlobalCssVars.ts'
import { useRightPanel } from '@/core/right-panel/RightPanelContext.tsx'
import { EditObjectPanelPage } from '@/objects/edit-object-panel/EditObjectPanelPage.tsx'

const cellBaseStyle = {
  padding: `${config.cellPaddingY}px ${config.cellPaddingX}px`,
  fontSize: 13,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
}

const rowSx = {
  '&:hover': { bgcolor: 'action.hover' },
  position: 'relative',
}
const rowStyle = { display: 'grid', gridTemplateColumns: 'subgrid', gridColumn: '1 / -1' }
const cellSx = {
  '&:hover': {
    borderRadius: globalCssVars.radius3,
    outline: '1.5px solid',
    outlineColor: 'primary.light',
    outlineOffset: '-1.5px',
  },
}
const dividerSx = { gridColumn: '1 / -1' }

export const GridRow = memo(function GridRow({
  obj,
  orderedColumns,
}: {
  obj: ObjectModel
  orderedColumns: PropertyColumn[]
}) {
  const objService = useDiRef<ObjectsService>('ObjectsService')
  const { openPanel } = useRightPanel()

  const onPropChange = useCallback(
    (propDefId: number, value: PropertyValue) => {
      void objService.updateObjectProperty(obj, propDefId, value)
    },
    [obj, objService],
  )

  function onExpand() {
    openPanel(<EditObjectPanelPage key={obj.id} object={obj} />)
  }

  return (
    <Box sx={rowSx} style={rowStyle}>
      {orderedColumns.map((col, idx) => (
        <Box
          key={col.propDef.id}
          style={cellBaseStyle}
          sx={idx === 0 ? { cursor: 'pointer', '&:hover': { color: 'primary.main' } } : cellSx}
          onClick={idx === 0 ? onExpand : undefined}
        >
          {idx === 0 ? (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {(obj.propertyValues.get(col.propDef.id) as string | undefined) || '—'}
            </span>
          ) : (
            <GridCell
              propDef={col.propDef}
              value={obj.propertyValues.get(col.propDef.id)}
              onChange={(v) => onPropChange(col.propDef.id, v)}
            />
          )}
        </Box>
      ))}
      <Divider sx={dividerSx} />
    </Box>
  )
})

export const SkeletonRow = memo(function SkeletonRow({
  rowIdx,
  orderedColumns,
  gridTemplateColumns,
}: {
  rowIdx: number
  orderedColumns: PropertyColumn[]
  gridTemplateColumns: string
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns }}>
      {orderedColumns.map((col) => (
        <div key={`skel-${rowIdx}-${col.propDef.id}`} style={cellBaseStyle}>
          <Skeleton variant="text" sx={{ fontSize: 13, width: '100%' }} />
        </div>
      ))}
    </div>
  )
})
